import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Grid,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    FormHelperText,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormLabel,
    Checkbox,
} from '@mui/material';
import api from '../../axios/api';
import CustomButton from '../../componentsII/Buttons';
import { useSelector } from 'react-redux';

const formSchema = yup.object().shape({
    locationName: yup.string().min(2, "Location name is required").required("Location name is required"),
    address: yup.string().required("Address is required"),
    country: yup.string().required("Country is required"),
    city: yup.string().required("City is required"),
    state: yup.string().required("State is required"),
    zipCode: yup.string().required("Zip code is required").max(10, "Zip code cannot be more than 10 characters"),
    features: yup.array().of(yup.string()).optional(),
    grossArea: yup.string().required("Gross area is required").max(10, "Invalid Gross area"),
    areaUnit: yup.string().required("Unit is required"),
    primaryUse: yup.string().required("Primary use is required"),
    subtype_usage_id: yup.string().required("Occupancy type is required"),
    usingNaturalGas: yup.string().required("Natural gas usage is required"),
    usingHeatSteam: yup.string().required("Heat/steam usage is required"),
    usingPurchasedCooling: yup.string().required("Purchased cooling usage is required"),
});

export const AddLocationModal = ({ isOpen, initialData = null, onSubmit: handleSubmit, mode = 'create' }) => {
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [primaryUses, setPrimaryUses] = useState([]);
    const [unit, setUnit] = useState([]);
    const [primaryUsageTypes, setPrimaryUsageTypes] = useState([]);
    const year = useSelector((state) => state.user.selectedYear);
    const user = useSelector((state) => state.user.user);

    const { control, handleSubmit: handleFormSubmit, reset, formState: { errors } } = useForm({
        resolver: yupResolver(formSchema),
        defaultValues: {
            locationName: initialData?.locationName || "",
            comp_data_year_id: year.id,
            address: initialData?.address || "",
            country: initialData?.country || "",
            city: initialData?.city || "",
            state: initialData?.state || "",
            zipCode: initialData?.zipCode || "",
            features: initialData?.features || [],
            grossArea: initialData?.grossArea || "",
            areaUnit: initialData?.areaUnit || "",
            primaryUse: initialData?.primaryUse || "",
            subtype_usage_id: initialData?.subtype_usage_id || "",
            usingNaturalGas: initialData?.usingNaturalGas || "0",
            usingHeatSteam: initialData?.usingHeatSteam || "0",
            usingPurchasedCooling: initialData?.usingPurchasedCooling || "0",
            Files: initialData?.Files || []
        }
    });


    const onFormSubmit = async (values) => {
        const locationData = {
            id: initialData?.id || "0",
            company_id: user?.company?.id || "",
            comp_data_year_id: year.id,
            loc_name: values.locationName,
            loc_short_code: values.locationName.substring(0, 5).toUpperCase(),
            country_id: values.country.toString(),
            state_id: values.state,
            city_name: values.city,
            address_line_1: values.address,
            address_line_2: "",
            zipcode: values.zipCode,
            using_natural_gas: values.usingNaturalGas,
            using_heat_steam: values.usingHeatSteam,
            using_purchased_cooling: values.usingPurchasedCooling,
            gross_area: values.grossArea || "0",
            gross_area_unit: values.areaUnit,
            primary_usage_id: values.primaryUse,
            subtype_usage_id: values.subtype_usage_id,
            created_by_userid: user.id,
        };
        try {
            const response = await api.post("/company/addupdatecompanylocation", locationData);
            if (response.data.status === "200") {
                handleSubmit?.(response.data);
            } else {
                handleSubmit?.({ status: "error", data: response.data });
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            handleSubmit?.({
                status: "error",
                data: { message: error.message || 'An error occurred' }
            });
        }
    };


    const handleReset = () => {
        reset(initialData);
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const countryResponse = await api.get("/common/countryDrop");
                const primaryUseResponse = await api.get("/common/locationprimaryusage");
                const areaUnitResponse = await api.get("/common/measurementtype?type_code=AREA");

                setCountries(countryResponse.data.data);
                setPrimaryUses(primaryUseResponse.data.data);
                setUnit(areaUnitResponse.data.data);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };
        fetchData();
    }, []);

    const handlePrimaryUseChange = async (value) => {
        if (value) {
            try {
                const primaryUseTypeResponse = await api.get(`/common/locationprimaryusagetype?primary_usage_id=${value}`);
                setPrimaryUsageTypes(primaryUseTypeResponse.data.data);
            } catch (error) {
                console.error('Error fetching primary usage types:', error);
            }
        }
    };

    const handleStateChange = async (countryId) => {
        if (countryId) {
            try {
                const res = await api.get(`/common/statedrop?country_id=${countryId}`);
                setStates(res.data.data);
            } catch (error) {
                setStates([]);
                console.error('Error fetching States:', error);
            }
        }
    };

    useEffect(() => {
        if (initialData) {
            reset(initialData);
            if (initialData.country) {
                handleStateChange(initialData.country);
            }
            if (initialData.primaryUse) {
                handlePrimaryUseChange(initialData.primaryUse);
            }
        } else {
            reset({
                locationName: "",
                address: "",
                country: "",
                city: "",
                state: "",
                zipCode: "",
                features: [],
                grossArea: "",
                areaUnit: "",
                primaryUse: "",
                subtype_usage_id: "",
                usingNaturalGas: "0",
                usingHeatSteam: "0",
                usingPurchasedCooling: "0",
                Files: []
            });
        }
    }, [initialData, reset]);

    return (
        <div className="w-[800px] mx-auto bg-white rounded-lg"  >
            <CardContent>
                <h5 className='border-b text-2xl font-medium text-green-600 pb-4 px-4'>
                    {mode === 'create' ? 'Add a Location' : 'Edit Location'}
                </h5>

                <div className='max-h-fit overflow-y-auto relative bg-white' style={{height: "calc(100vh - 170px)"}}>
                    <form onSubmit={handleFormSubmit(onFormSubmit)} className='pt-4 px-4'>
                    <Grid container spacing={3}>
                        {/* Location Name */}
                        <Grid item xs={12}>
                            <Controller
                                name="locationName"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label={
                                            <span>
                                                Location Name
                                                <span style={{ color: 'red' }}> *</span>
                                            </span>
                                        }
                                        error={!!errors.locationName}
                                        helperText={errors.locationName?.message}
                                    />
                                )}
                            />
                        </Grid>


                        {/* Address */}
                        <Grid item xs={12}>
                            <Controller
                                name="address"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        multiline
                                        rows={2}
                                        label={
                                            <span>
                                                Complete Address
                                                <span style={{ color: 'red' }}> *</span>
                                            </span>
                                        }
                                        error={!!errors.address}
                                        helperText={errors.address?.message}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Location Details */}
                        <Grid item xs={3}>
                            <Controller
                                name="country"
                                control={control}
                                render={({ field }) => (
                                    <FormControl fullWidth error={!!errors.country}>
                                        <InputLabel>Select Country<span style={{ color: 'red' }}> *</span></InputLabel>
                                        <Select
                                            {...field}
                                            label={
                                                <span>
                                                    Country
                                                    <span style={{ color: 'red' }}> *</span>
                                                </span>
                                            }
                                            onChange={(e) => {
                                                field.onChange(e);
                                                handleStateChange(e.target.value);
                                                reset({ ...control._formValues, state: "" });
                                            }}
                                        >
                                            {countries.map((country) => (
                                                <MenuItem key={country.value} value={country.value}>
                                                    {country.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText>{errors.country?.message}</FormHelperText>
                                    </FormControl>
                                )}
                            />
                        </Grid>

                        <Grid item xs={3}>
                            <Controller
                                name="state"
                                control={control}
                                render={({ field }) => (
                                    <FormControl fullWidth error={!!errors.state}>
                                        <InputLabel>Select State<span style={{ color: 'red' }}> *</span></InputLabel>
                                        <Select {...field} label="Select State" disabled={states.length === 0}>
                                            {states.map((state) => (
                                                <MenuItem key={state.value} value={state.value}>
                                                    {state.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText>{errors.state?.message}</FormHelperText>
                                    </FormControl>
                                )}
                            />
                        </Grid>

                        <Grid item xs={3}>
                            <Controller
                                name="city"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label={
                                            <span>
                                                City
                                                <span style={{ color: 'red' }}> *</span>
                                            </span>
                                        }
                                        error={!!errors.city}
                                        helperText={errors.city?.message}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={3}>
                            <Controller
                                name="zipCode"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label={
                                            <span>
                                                Zip Code
                                                <span style={{ color: 'red' }}> *</span>
                                            </span>
                                        }
                                        error={!!errors.zipCode}
                                        helperText={errors.zipCode?.message}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Gross Area and Unit, Primary Use and Occupancy Type */}
                        <Grid item xs={3}>
                            <Controller
                                name="primaryUse"
                                control={control}
                                render={({ field }) => (
                                    <FormControl fullWidth error={!!errors.primaryUse}>
                                        <InputLabel>Primary Use<span style={{ color: 'red' }}> *</span></InputLabel>
                                        <Select
                                            {...field}
                                            label={
                                                <span>
                                                    Primary Use
                                                    <span style={{ color: 'red' }}> *</span>
                                                </span>
                                            }
                                            onChange={(e) => {
                                                field.onChange(e);
                                                handlePrimaryUseChange(e.target.value);
                                            }}
                                        >
                                            {primaryUses.map((primaryUse) => (
                                                <MenuItem key={primaryUse.value} value={primaryUse.value}>
                                                    {primaryUse.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText>{errors.primaryUse?.message}</FormHelperText>
                                    </FormControl>
                                )}
                            />
                        </Grid>

                        <Grid item xs={3}>
                            <Controller
                                name="subtype_usage_id"
                                control={control}
                                render={({ field }) => (
                                    <FormControl fullWidth error={!!errors.subtype_usage_id}>
                                        <InputLabel>Primary Use Type<span style={{ color: 'red' }}> *</span></InputLabel>
                                        <Select {...field} label="Primary Use Type" disabled={primaryUsageTypes.length === 0}>
                                            {primaryUsageTypes.map((primaryUseType) => (
                                                <MenuItem key={primaryUseType.value} value={primaryUseType.value}>
                                                    {primaryUseType.label}</MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText>{errors.primaryUse?.message}</FormHelperText>
                                    </FormControl>
                                )}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                name="grossArea"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        type="number"
                                        label={
                                            <span>
                                                Gross Area
                                                <span style={{ color: 'red' }}> *</span>
                                            </span>
                                        }
                                        error={!!errors.grossArea}
                                        helperText={errors.grossArea?.message}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={3}>
                            <Controller
                                name="areaUnit"
                                control={control}
                                render={({ field }) => (
                                    <FormControl fullWidth error={!!errors.areaUnit}>
                                        <InputLabel>Unit<span style={{ color: 'red' }}> *</span></InputLabel>
                                        <Select {...field} label="Unit" disabled={unit.length === 0}>
                                            {unit.map((unit) => (
                                                <MenuItem key={unit.value} value={unit.value}>
                                                    {unit.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText>{errors.areaUnit?.message}</FormHelperText>
                                    </FormControl>
                                )}
                            />
                        </Grid>


                        {/* Utility Usage Section */}
                        <Grid item xs={12}>
                            <FormLabel component="legend" sx={{
                                fontSize: '1.4rem',
                                fontWeight: 'bold',
                                marginBottom: '0.5rem'
                            }}>
                                In addition to electricity, does your location use any of the following utilities?
                            </FormLabel>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Controller
                                        name="usingNaturalGas"
                                        control={control}
                                        render={({ field }) => (
                                            <FormControl error={!!errors.usingNaturalGas}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={field.value === "1"}
                                                            onChange={(e) => field.onChange(e.target.checked ? "1" : "0")}
                                                        />
                                                    }
                                                    label={
                                                        <Typography variant="body2">
                                                            This location also uses natural gas
                                                        </Typography>
                                                    }
                                                    sx={{
                                                        fontSize: '1.5rem',
                                                        marginBottom: '-1rem'
                                                    }}
                                                />
                                            </FormControl>
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Controller
                                        name="usingHeatSteam"
                                        control={control}
                                        render={({ field }) => (
                                            <FormControl error={!!errors.usingHeatSteam} sx={{
                                                marginBottom: '-1rem',
                                            }}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={field.value === "1"}
                                                            onChange={(e) => field.onChange(e.target.checked ? "1" : "0")}
                                                        />
                                                    }
                                                    label={
                                                        <Typography variant="body2">
                                                            This location also uses heat and steam
                                                        </Typography>
                                                    }
                                                    sx={{
                                                        fontSize: '1.5rem',
                                                    }}
                                                />
                                                <FormHelperText >{'These emissions are associated with generating heat and steam off -site by a third-party supplier and being delivered to your locations.'}</FormHelperText>
                                            </FormControl>
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Controller
                                        name="usingPurchasedCooling"
                                        control={control}
                                        render={({ field }) => (
                                            <FormControl error={!!errors.usingPurchasedCooling}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={field.value === "1"}
                                                            onChange={(e) => field.onChange(e.target.checked ? "1" : "0")}
                                                        />
                                                    }
                                                    label={
                                                        <Typography variant="body2">
                                                            This location also uses purchased cooling
                                                        </Typography>
                                                    }
                                                    sx={{
                                                        fontSize: '1.5rem',
                                                    }}
                                                />
                                                <FormHelperText>{'purchased cooling is generally used by < 3% of locations and is separated line item in your utility  bill for cooling delivered by a third party supplier through specialized chillers or other means'}</FormHelperText>
                                            </FormControl>
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <div className="w-full flex justify-end gap-5 items-center sticky bg-white border-t pt-4" style={{bottom:0}}>
                            <CustomButton
                                className="bg-gray-500 hover:bg-gray-700 text-white "
                                click={() => handleReset()}
                                text="Reset"
                            />
                            <CustomButton
                                className="bg-green-600 hover:bg-green-700 text-white "
                                type="submit"
                                text=" Add Location"
                            />
                        </div>
                    </Grid>
                </form></div>
            </CardContent>
        </div>
    );
};

export default AddLocationModal;